import { useApolloClient } from "@apollo/client";
import { LabeledRichTextValue, LabeledValue } from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ContentState, RawDraftContentState, convertToRaw } from "draft-js";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { isEqual } from "lodash-es";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { RichTextEditor } from "@msys/ui";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsAboutBox_ViewerOrganisationFragment,
  useOrganisationDetailsAboutBox_ModifyOrganisationMutation,
} from "./OrganisationDetailsAboutBox.generated.js";
interface Props {
  organisation: OrganisationDetailsAboutBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}

interface AboutCompanyFormValues {
  description: string;
  industry: string;
  about: RawDraftContentState;
  yearFounded: number | undefined;
  employeesNumber: number | undefined;
}

function normalizeYearFounded(value: number | undefined): number | null {
  if (typeof value !== "number" || !Number.isInteger(value)) return null;
  if (!/^\d{4}$/.test(String(value))) return null;
  if (value < 1800 || value > new Date().getFullYear()) return null;
  return value;
}

function AboutForm({ organisation, isViewerOrganisation }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "OrganisationProfile",
    "OrganisationPublicProfile",
    "Global",
  ]);
  const client = useApolloClient();

  const [modifyOrganisation] =
    useOrganisationDetailsAboutBox_ModifyOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationDetails],
    });

  const initialValues: AboutCompanyFormValues = React.useMemo(
    () => ({
      description: organisation.description,
      industry: organisation.industry ?? "",
      about:
        (organisation.about as RawDraftContentState | undefined) ??
        convertToRaw(ContentState.createFromText("")),
      yearFounded: organisation.yearFounded ?? undefined,
      employeesNumber: organisation.employeesNumber ?? undefined,
    }),
    [
      organisation.description,
      organisation.industry,
      organisation.about,
      organisation.yearFounded,
      organisation.employeesNumber,
    ]
  );

  const validationSchema = Yup.object().shape({
    yearFounded: Yup.number()
      .label(t("Year founded", { ns: "OrganisationPublicProfile" }))
      .nullable()
      .integer()
      .test(
        "YYYY format",
        t("Year founded must be in YYYY format", {
          ns: "OrganisationProfile",
        }),
        value => value == null || /^\d{4}$/.test(String(value))
      )
      .min(
        1800,
        t("Year founded cannot be older than 1800", {
          ns: "OrganisationProfile",
        })
      )
      .max(new Date().getFullYear()),
    employeesNumber: Yup.number()
      .label(t("Number of employees", { ns: "OrganisationPublicProfile" }))
      .nullable()
      .min(0),
  });

  const handleSubmit = async (values: AboutCompanyFormValues) => {
    try {
      const safeYearFounded = normalizeYearFounded(values.yearFounded);

      await modifyOrganisation({
        variables: {
          input: {
            ...(isViewerOrganisation
              ? {}
              : { organisationId: organisation.id }),
            description: values.description,
            industry: values.industry,
            about: values.about,
            yearFounded: safeYearFounded,
            employeesNumber: values.employeesNumber ?? null,
          },
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  return (
    <Formik<AboutCompanyFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <Form>
          <Stack spacing={1}>
            <TextField
              name="description"
              label={t("Short description", { ns: "OrganisationProfile" })}
              disabled={false}
              multiline
            />
            <RichTextEditor
              label={t("About the Organisation", { ns: "OrganisationProfile" })}
              content={formikProps.values.about}
              canEdit={true}
              onChange={(content: RawDraftContentState) => {
                if (!isEqual(formikProps.values.about, content)) {
                  formikProps.setFieldValue("about", content);
                }
              }}
              saveButtonLabel={t("Save", { ns: "Global" })}
              cancelButtonLabel={t("Cancel", { ns: "Global" })}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                name="yearFounded"
                label={t("Year founded", {
                  ns: "OrganisationPublicProfile",
                })}
                type="number"
                slotProps={{
                  htmlInput: { min: 1800, max: new Date().getFullYear() },
                }}
              />
              <TextField
                name="employeesNumber"
                label={t("Number of employees", {
                  ns: "OrganisationPublicProfile",
                })}
                type="number"
                slotProps={{ htmlInput: { min: 0, max: 99999 } }}
              />
            </Stack>
            <TextField
              name="industry"
              label={t("Industry", {
                ns: "OrganisationProfile",
              })}
              placeholder={t("Industry placeholder", {
                ns: "OrganisationProfile",
              })}
            />
            <AutoSave />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}

function AboutView({
  organisation,
}: {
  organisation: OrganisationDetailsAboutBox_ViewerOrganisationFragment;
}) {
  const { t } = useTranslate([
    "OrganisationProfile",
    "OrganisationPublicProfile",
    "Global",
  ]);

  return (
    <>
      <LabeledValue
        label={t("Short description", { ns: "OrganisationProfile" })}
      >
        {organisation.description || t("Not set", { ns: "Global" })}
      </LabeledValue>
      <LabeledValue
        label={t("About the Organisation", { ns: "OrganisationProfile" })}
      >
        <LabeledRichTextValue
          placeholder={t("Not set", { ns: "Global" })}
          content={organisation.about as RawDraftContentState | undefined}
        />
      </LabeledValue>
      <LabeledValue
        label={t("Year founded", {
          ns: "OrganisationPublicProfile",
        })}
      >
        {organisation.yearFounded ?? t("Not set", { ns: "Global" })}
      </LabeledValue>
      <LabeledValue
        label={t("Number of employees", {
          ns: "OrganisationPublicProfile",
        })}
      >
        {organisation.employeesNumber ?? t("Not set", { ns: "Global" })}
      </LabeledValue>
      <LabeledValue
        label={t("Industry", {
          ns: "OrganisationProfile",
        })}
      >
        {organisation.industry || t("Not set", { ns: "Global" })}
      </LabeledValue>
    </>
  );
}

export function OrganisationDetailsAboutBox({
  organisation,
  isViewerOrganisation,
}: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const viewer = useUserData().currentUser!;

  return (
    <SectionCard
      title={t("About", { ns: "OrganisationProfile" })}
      titleAdornment={
        <Tooltip
          title={t("About section tooltip", {
            ns: "OrganisationProfile",
          })}
        >
          <IconButton color="secondary" aria-label="Show helper">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <RestrictedByOrganisationPermissionWithDebug
        permission="organisation_profile_update"
        otherwise={<AboutView organisation={organisation} />}
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <AboutForm
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
          />
        ) : (
          <AboutView organisation={organisation} />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}
